/**
 * Sync PDP recommendation cards (related + complementary) to the
 * currently selected main-product color (Cor).
 */
(function () {
  function normalizeColor(value) {
    return String(value || '')
      .toLowerCase()
      .trim()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  function colorsMatch(a, b) {
    const left = normalizeColor(a);
    const right = normalizeColor(b);
    if (!left || !right) return false;
    if (left === right) return true;
    return left.includes(right) || right.includes(left);
  }

  function formatMoney(cents) {
    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      const moneyFormat =
        window.theme?.moneyFormat ||
        window.Shopify?.money_format ||
        'R$ {{amount_with_comma_separator}}';
      return Shopify.formatMoney(cents, moneyFormat);
    }

    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format((Number(cents) || 0) / 100);
  }

  function parseVariants(card) {
    const jsonEl = card.querySelector('[data-variants-json]');
    if (!jsonEl) return [];

    try {
      return JSON.parse(jsonEl.textContent);
    } catch (error) {
      console.error('recommendation-color-sync: failed to parse variants JSON', error);
      return [];
    }
  }

  function getColorOptionPosition(productInfo) {
    const position = productInfo?.getAttribute('data-color-option-position');
    if (!position) return null;
    const parsed = Number.parseInt(position, 10);
    return Number.isFinite(parsed) ? parsed : null;
  }

  function getColorFromVariant(variant, colorPosition) {
    if (!variant || !colorPosition) return null;
    if (colorPosition === 1) return variant.option1 || null;
    if (colorPosition === 2) return variant.option2 || null;
    if (colorPosition === 3) return variant.option3 || null;
    return null;
  }

  function getSelectedColorFromPdp() {
    const productInfo = document.querySelector('product-info');
    if (!productInfo) return null;

    const colorPosition = getColorOptionPosition(productInfo);
    const variantEl = productInfo.querySelector('variant-selects [data-selected-variant]');

    if (variantEl?.textContent) {
      try {
        const variant = JSON.parse(variantEl.textContent);
        const color = getColorFromVariant(variant, colorPosition);
        if (color) return color;
      } catch (error) {
        console.error('recommendation-color-sync: failed to parse selected variant', error);
      }
    }

    const checkedColorInput = productInfo.querySelector(
      '.zuck-color-group input[type="radio"]:checked, variant-selects fieldset.product-form__input--swatch input:checked'
    );
    if (checkedColorInput?.value) return checkedColorInput.value;

    return null;
  }

  function getRecommendationCards(root) {
    const scope = root && root.querySelectorAll ? root : document;
    const containers = scope.querySelectorAll(
      'product-recommendations.product-recs-color-sync, product-recommendations.related-products, product-recommendations.complementary-products'
    );

    const cards = [];
    containers.forEach(function (container) {
      container.querySelectorAll('.product-card-wrapper').forEach(function (card) {
        if (card.closest('[data-compre-junto-item]')) return;
        cards.push(card);
      });
    });

    return cards;
  }

  function findBestVariantByColor(variants, selectedColor) {
    if (!variants.length) return null;

    const exact = variants.find(function (variant) {
      return normalizeColor(variant.cor) === normalizeColor(selectedColor);
    });
    if (exact) return exact;

    const partial = variants.find(function (variant) {
      return colorsMatch(variant.cor, selectedColor);
    });
    if (partial) return partial;

    return variants.find(function (variant) {
      return variant.available;
    }) || variants[0];
  }

  function applyColorToCompreJuntoItem(item, selectedColor) {
    const variants = parseVariants(item).filter(function (variant) {
      return variant.available !== false;
    });
    const best = findBestVariantByColor(variants, selectedColor);
    if (!best) return;

    const colorSwatch = Array.from(item.querySelectorAll('[data-compre-junto-color-swatch]')).find(function (button) {
      return colorsMatch(button.getAttribute('data-color'), selectedColor);
    });

    if (colorSwatch && window.CompreJunto && typeof window.CompreJunto.selectColor === 'function') {
      window.CompreJunto.selectColor(item, colorSwatch.getAttribute('data-color') || '');
      return;
    }

    const option = item.querySelector(
      '[data-compre-junto-picker-option][data-variant-id="' + best.id + '"]'
    );
    if (!option) return;

    if (window.CompreJunto && typeof window.CompreJunto.selectVariantOption === 'function') {
      window.CompreJunto.selectVariantOption(item, option);
    }
  }

  function syncCompreJuntoItems(selectedColor, root) {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('[data-compre-junto-item]').forEach(function (item) {
      if (item.hasAttribute('data-compre-junto-hidden')) return;
      applyColorToCompreJuntoItem(item, selectedColor);
    });
  }

  function findBestSwatchButton(card, selectedColor) {
    const buttons = Array.from(card.querySelectorAll('.card-product-swatch-btn'));
    if (!buttons.length) return null;

    const exact = buttons.find(function (button) {
      return normalizeColor(button.getAttribute('data-title')) === normalizeColor(selectedColor);
    });
    if (exact) return exact;

    const partial = buttons.find(function (button) {
      return colorsMatch(button.getAttribute('data-title'), selectedColor);
    });
    if (partial) return partial;

    return buttons.find(function (button) {
      return button.getAttribute('data-variant-id');
    });
  }

  function setActiveSwatch(card, button) {
    card.querySelectorAll('.card-product-swatch-btn.is-active').forEach(function (activeButton) {
      activeButton.classList.remove('is-active');
    });
    button.classList.add('is-active');
  }

  function updateCardImage(card, button) {
    const img = card.querySelector('[data-card-image]');
    if (!img) return;

    const src = button.getAttribute('data-image-src');
    const srcset = button.getAttribute('data-image-srcset');
    const alt = button.getAttribute('data-image-alt');

    if (src) img.src = src;
    if (srcset) img.srcset = srcset;
    if (alt !== null) img.alt = alt;
  }

  function getInstallmentDivisor(price) {
    if (price < 10000) return 1;
    if (price < 15000) return 2;
    return 3;
  }

  function updateCardCustomPrice(card, variant) {
    const price = Number(variant.price || 0);
    const compareAt = Number(variant.compare_at_price || 0);
    const bundleCompare = Number(card.getAttribute('data-bundle-compare-at') || 0);
    const effectiveCompare = compareAt > price ? compareAt : bundleCompare > price ? bundleCompare : 0;
    const hasDiscount = effectiveCompare > price;

    const priceOld = card.querySelector('.custom-card-price .price-old');
    const priceMain = card.querySelector('.custom-card-price .price-main');
    const priceInstallments = card.querySelector('.custom-card-price .price-installments');
    const divisor = getInstallmentDivisor(price);

    if (priceMain) priceMain.textContent = formatMoney(price);
    if (priceInstallments) {
      priceInstallments.textContent = 'ou ' + divisor + 'x de ' + formatMoney(Math.round(price / divisor));
    }

    if (priceOld) {
      if (hasDiscount) {
        priceOld.textContent = formatMoney(effectiveCompare);
        priceOld.classList.remove('hidden');
        priceOld.style.display = '';
      } else {
        priceOld.textContent = '';
        priceOld.style.display = 'none';
      }
    }

    const badge = card.querySelector('.product-badge-discount');
    if (badge) {
      if (hasDiscount) {
        const discountPercent = Math.round(((effectiveCompare - price) * 100) / effectiveCompare);
        badge.textContent = discountPercent + '% OFF';
        badge.classList.remove('hidden');
        badge.style.display = '';
      } else {
        badge.textContent = '';
        badge.style.display = 'none';
      }
    }
  }

  function updateCardLinks(card, variant) {
    const url = variant?.url;
    if (!url) return;

    card.querySelectorAll('a[href*="/products/"]').forEach(function (link) {
      link.setAttribute('href', url);
    });
  }

  function applyColorToCard(card, selectedColor) {
    const button = findBestSwatchButton(card, selectedColor);
    if (!button) return;

    setActiveSwatch(card, button);
    updateCardImage(card, button);

    const variantId = button.getAttribute('data-variant-id');
    if (!variantId) return;

    const variants = parseVariants(card);
    const variant = variants.find(function (entry) {
      return String(entry.id) === String(variantId);
    });

    if (variant) {
      updateCardLinks(card, variant);
      updateCardCustomPrice(card, variant);
    }
  }

  function syncRecommendationCards(selectedColor, root) {
    if (!selectedColor) return;

    syncCompreJuntoItems(selectedColor, root);

    getRecommendationCards(root).forEach(function (card) {
      applyColorToCard(card, selectedColor);
    });
  }

  function syncFromPdp(root) {
    syncRecommendationCards(getSelectedColorFromPdp(), root);
  }

  function init() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
        const variant = event?.data?.variant;
        if (!variant) return;

        const productInfo = document.querySelector('product-info');
        const color = getColorFromVariant(variant, getColorOptionPosition(productInfo));
        if (color) syncRecommendationCards(color);
      });
    }

    syncFromPdp();

    document.addEventListener('recommendations:loaded', function (event) {
      const root = event?.detail?.container || document;
      syncFromPdp(root);
    });
  }

  window.RecommendationColorSync = {
    syncFromPdp: syncFromPdp,
    syncRecommendationCards: syncRecommendationCards,
    getSelectedColorFromPdp: getSelectedColorFromPdp,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
